import { useState } from "react";
import { useLang } from "@/lib/i18n";
import { useCatalog } from "@/lib/catalog";
import { PageHead, CardSkeleton, ErrorState, EmptyState } from "@/components/Ui";
import { Reveal } from "@/components/Reveal";
import { DynIcon } from "@/components/DynIcon";

export default function Modules() {
  const { t, lang } = useLang();
  const { catalog, loading, error, reload } = useCatalog();
  const [tab, setTab] = useState("general");

  const modules = (catalog?.modules || []).filter((m) => m.category === tab);
  const counts = {
    general: (catalog?.modules || []).filter((m) => m.category === "general").length,
    sector: (catalog?.modules || []).filter((m) => m.category === "sector").length,
  };

  return (
    <div className="bg-grid" data-testid="modules-page">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <PageHead eyebrow={t("modules.eyebrow")} title={t("modules.title")} sub={t("modules.sub")} testid="modules-head" />

        <div className="mt-10 flex justify-center gap-2" data-testid="modules-tabs">
          {[
            { key: "general", label: t("modules.general") },
            { key: "sector", label: t("modules.sector") },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`tab-${key}`}
              className={`rounded-lg px-5 py-2.5 text-sm font-semibold transition-colors ${
                tab === key ? "bg-marine text-white" : "border border-slate-200 bg-white text-slate-600 hover:border-brand hover:text-brand"
              }`}
            >
              {label}
              {catalog && <span className="ml-2 rounded-full bg-gold/20 px-2 py-0.5 text-xs text-gold">{counts[key]}</span>}
            </button>
          ))}
        </div>

        <div className="mt-10">
          {loading ? (
            <CardSkeleton count={9} />
          ) : error ? (
            <ErrorState onRetry={reload} />
          ) : modules.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="modules-grid">
              {modules.map((m, i) => (
                <Reveal key={m.key} delay={(i % 3) * 80}>
                  <div className="mg-card group h-full p-6 transition-shadow hover:shadow-lg hover:shadow-marine/5" data-testid={`module-card-${m.key}`}>
                    <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-brand/10 text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                      <DynIcon name={m.icon} className="h-5 w-5" />
                    </div>
                    <h3 className="mt-4 font-display text-lg font-bold text-marine">{lang === "fr" ? m.name_fr : m.name_en}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-500">{lang === "fr" ? m.desc_fr : m.desc_en}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
